/**
 * Navigation Component with Disclosure Pattern
 *
 * Implements WAI-ARIA disclosure navigation pattern
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/
 *
 * @package Commcore26
 */

export const MobileNav = {
	// Breakpoint for desktop navigation
	desktopBreakpoint: 992,

	// Store references
	topLevelButtons: [],
	menuItemsWithChildren: [],

	/**
	 * Initialize navigation
	 */
	init() {
		this.nav = document.querySelector('.main-navigation');
		this.toggle = document.querySelector('.menu-toggle');

		if (!this.nav) {
			return;
		}

		this.setupDisclosureButtons();
		this.setupSearchToggle();
		this.bindEvents();
		this.setCurrentPage();
		this.updateTabIndex();
	},

	/**
	 * Create disclosure buttons for menu items with children
	 * Converts the parent link into a button to match WAI-ARIA pattern
	 */
	setupDisclosureButtons() {
		this.menuItemsWithChildren = Array.from(
			this.nav.querySelectorAll(':scope > ul > .menu-item-has-children')
		);

		this.menuItemsWithChildren.forEach((item, index) => {
			const link = item.querySelector(':scope > a');
			const submenu = item.querySelector(':scope > .sub-menu');

			if (!link || !submenu) return;

			// Generate unique ID for submenu
			const submenuId = `submenu-${index}`;
			submenu.id = submenuId;

			// Create disclosure button with the link text
			const button = document.createElement('button');
			button.setAttribute('type', 'button');
			button.setAttribute('aria-expanded', 'false');
			button.setAttribute('aria-controls', submenuId);
			button.classList.add('submenu-toggle');
			button.textContent = link.textContent;

			// Store the original href for potential use
			button.dataset.href = link.href;

			// Replace link with button
			link.replaceWith(button);

			// Store reference
			item.disclosureButton = button;
			item.submenu = submenu;
		});

		// Cache top-level buttons
		this.topLevelButtons = Array.from(
			this.nav.querySelectorAll(':scope > ul > li > .submenu-toggle')
		);
	},

	/**
	 * Set up the nav search toggle button.
	 */
	setupSearchToggle() {
		this.searchToggle = this.nav.querySelector('.nav-search-toggle');
		this.searchForm = this.nav.querySelector('.nav-search-form');

		if (!this.searchToggle || !this.searchForm) return;

		this.searchToggle.addEventListener('click', (e) => {
			e.stopPropagation();
			const isOpen = this.searchToggle.getAttribute('aria-expanded') === 'true';
			if (isOpen) {
				this.closeSearch();
			} else {
				this.closeAllSubmenus();
				this.openSearch();
			}
		});

		// Close search on Escape from within the form
		this.searchForm.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				this.closeSearch();
				this.searchToggle.focus();
			}
		});
	},

	/**
	 * Open nav search form.
	 */
	openSearch() {
		if (!this.searchToggle) return;
		this.searchToggle.setAttribute('aria-expanded', 'true');
		this.searchForm.hidden = false;
		const input = this.searchForm.querySelector('.search-field');
		input?.focus();
	},

	/**
	 * Close nav search form.
	 */
	closeSearch() {
		if (!this.searchToggle) return;
		this.searchToggle.setAttribute('aria-expanded', 'false');
		this.searchForm.hidden = true;
	},

	/**
	 * Update tabindex based on viewport
	 */
	updateTabIndex() {
		// Links in submenus should be tabbable when submenu is open
		// This is handled by display:none/block on the submenu
	},

	/**
	 * Bind event listeners
	 */
	bindEvents() {
		// Mobile menu toggle
		if (this.toggle) {
			this.toggle.addEventListener('click', () => this.toggleMobileMenu());
		}

		// Disclosure button clicks
		this.nav.addEventListener('click', (e) => {
			const button = e.target.closest('.submenu-toggle');
			if (button) {
				e.preventDefault();
				this.toggleSubmenu(button);
			}
		});

		// Keyboard handling on nav
		this.nav.addEventListener('keydown', (e) => this.handleKeydown(e));

		// Close when clicking outside
		document.addEventListener('click', (e) => {
			if (!this.nav.contains(e.target) && !this.toggle?.contains(e.target)) {
				this.closeAllSubmenus();
				this.closeSearch();
				if (this.isMobileMenuOpen()) {
					this.closeMobileMenu();
				}
			}
		});

		// Close submenus on focusout
		this.nav.addEventListener('focusout', (e) => {
			// Delay to allow focus to move to new element
			setTimeout(() => {
				this.menuItemsWithChildren.forEach((item) => {
					if (!item.contains(document.activeElement)) {
						this.closeSubmenu(item.disclosureButton);
					}
				});
			}, 0);
		});

		// Handle window resize
		window.addEventListener(
			'resize',
			window.commcore26.debounce(() => {
				if (this.isDesktop() && this.isMobileMenuOpen()) {
					this.closeMobileMenu();
				}
			}, 100)
		);
	},

	/**
	 * Handle keydown events per WAI-ARIA disclosure nav pattern
	 */
	handleKeydown(e) {
		const { key } = e;
		const target = e.target;

		// Space or Enter on disclosure button - toggle dropdown
		if (key === 'Enter' || key === ' ') {
			if (target.classList.contains('submenu-toggle')) {
				e.preventDefault();
				this.toggleSubmenu(target);
				return;
			}

			// On a link - set aria-current and activate
			if (target.tagName === 'A') {
				const allLinks = this.nav.querySelectorAll('a[aria-current]');
				allLinks.forEach((link) => link.removeAttribute('aria-current'));
				target.setAttribute('aria-current', 'page');
				// Let link activate naturally
				return;
			}
		}

		// Escape - close dropdown and focus its button
		if (key === 'Escape') {
			if (target.closest('.sub-menu')) {
				const menuItem = target.closest('.menu-item-has-children');
				if (menuItem?.disclosureButton) {
					this.closeSubmenu(menuItem.disclosureButton);
					menuItem.disclosureButton.focus();
				}
				return;
			}
			// Close any open submenus
			this.closeAllSubmenus();
			return;
		}

		// Down Arrow or Right Arrow
		if (key === 'ArrowDown' || key === 'ArrowRight') {
			e.preventDefault();

			// On a disclosure button
			if (target.classList.contains('submenu-toggle')) {
				const isExpanded = target.getAttribute('aria-expanded') === 'true';
				const buttonIndex = this.topLevelButtons.indexOf(target);

				if (isExpanded) {
					// Move focus to first link in dropdown
					const submenu = document.getElementById(
						target.getAttribute('aria-controls')
					);
					const firstLink = submenu?.querySelector('a');
					firstLink?.focus();
				} else if (buttonIndex < this.topLevelButtons.length - 1) {
					// Move to next button (not last)
					this.topLevelButtons[buttonIndex + 1]?.focus();
				}
				return;
			}

			// On a link in submenu
			if (target.tagName === 'A' && target.closest('.sub-menu')) {
				const submenu = target.closest('.sub-menu');
				const links = Array.from(submenu.querySelectorAll(':scope > li > a'));
				const currentIndex = links.indexOf(target);

				if (currentIndex < links.length - 1) {
					links[currentIndex + 1]?.focus();
				}
				return;
			}
		}

		// Up Arrow or Left Arrow
		if (key === 'ArrowUp' || key === 'ArrowLeft') {
			e.preventDefault();

			// On a disclosure button
			if (target.classList.contains('submenu-toggle')) {
				const buttonIndex = this.topLevelButtons.indexOf(target);

				if (buttonIndex > 0) {
					this.topLevelButtons[buttonIndex - 1]?.focus();
				}
				return;
			}

			// On a link in submenu
			if (target.tagName === 'A' && target.closest('.sub-menu')) {
				const submenu = target.closest('.sub-menu');
				const links = Array.from(submenu.querySelectorAll(':scope > li > a'));
				const currentIndex = links.indexOf(target);

				if (currentIndex > 0) {
					links[currentIndex - 1]?.focus();
				}
				return;
			}
		}

		// Home - first button
		if (key === 'Home') {
			e.preventDefault();
			this.topLevelButtons[0]?.focus();
		}

		// End - last button
		if (key === 'End') {
			e.preventDefault();
			this.topLevelButtons[this.topLevelButtons.length - 1]?.focus();
		}
	},

	/**
	 * Toggle submenu
	 */
	toggleSubmenu(button) {
		const isExpanded = button.getAttribute('aria-expanded') === 'true';

		if (isExpanded) {
			this.closeSubmenu(button);
		} else {
			// Close other submenus at the same level
			this.topLevelButtons.forEach((btn) => {
				if (btn !== button) {
					this.closeSubmenu(btn);
				}
			});
			this.closeSearch();
			this.openSubmenu(button);
		}
	},

	/**
	 * Open submenu
	 */
	openSubmenu(button) {
		if (!button) return;

		button.setAttribute('aria-expanded', 'true');
		const menuItem = button.closest('.menu-item-has-children');
		menuItem?.classList.add('toggled');
	},

	/**
	 * Close submenu
	 */
	closeSubmenu(button) {
		if (!button) return;

		button.setAttribute('aria-expanded', 'false');
		const menuItem = button.closest('.menu-item-has-children');
		menuItem?.classList.remove('toggled');
	},

	/**
	 * Close all submenus
	 */
	closeAllSubmenus() {
		this.topLevelButtons.forEach((button) => this.closeSubmenu(button));
	},

	/**
	 * Toggle mobile menu
	 */
	toggleMobileMenu() {
		if (this.isMobileMenuOpen()) {
			this.closeMobileMenu();
		} else {
			this.openMobileMenu();
		}
	},

	/**
	 * Open mobile menu
	 */
	openMobileMenu() {
		this.nav.classList.add('toggled');
		this.toggle?.setAttribute('aria-expanded', 'true');
		document.body.classList.add('nav-open');

		// Focus first button
		const firstButton = this.nav.querySelector('.submenu-toggle');
		firstButton?.focus();
	},

	/**
	 * Close mobile menu
	 */
	closeMobileMenu() {
		this.nav.classList.remove('toggled');
		this.toggle?.setAttribute('aria-expanded', 'false');
		document.body.classList.remove('nav-open');
		this.closeAllSubmenus();
		this.toggle?.focus();
	},

	/**
	 * Check if mobile menu is open
	 */
	isMobileMenuOpen() {
		return this.nav.classList.contains('toggled');
	},

	/**
	 * Check if we're at desktop breakpoint
	 */
	isDesktop() {
		return window.innerWidth >= this.desktopBreakpoint;
	},

	/**
	 * Set aria-current on current page link
	 */
	setCurrentPage() {
		const currentUrl = window.location.href;
		const links = this.nav.querySelectorAll('a');

		links.forEach((link) => {
			if (link.href === currentUrl) {
				link.setAttribute('aria-current', 'page');
			}
		});
	},
};
